import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom-v5-compat";
import { useExperiments } from "@hopper-b2b/experiments";
import { ExperimentVariant } from "@hopper-b2b/types";
import { IconComponent, IconName } from "@hopper-b2b/ui";
import { useHotkeys } from "@hopper-b2b/utilities";
import {
  Fab,
  Box,
  List,
  ListItem,
  Divider,
  Drawer,
  Switch,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
} from "@material-ui/core";
import { inspect } from "@xstate/inspect";
import clsx from "clsx";
import { revokeUserAuth } from "../../checkout/states/payments/api/revokeUserAuth";
import { isExperimentVariantAvailable } from "../../utils/applyExperimentOverrides";
import { clearBookingSurveyCompletions } from "../../utils/csatSurvey";
import { DEBUG_EXPERIMENT_TOGGLES } from "../../utils/debugExperimentFlags";
import { useNubankFeatureFlagsContext } from "../../utils/featureFlagsContext";
import { useNubankExperimentOverrides } from "../NubankExperimentsOverrideBridge";
import { canAccessDebugMenu } from "./canAccessDebugMenu";
import styles from "./style.module.scss";

/**
 * Configuration for feature flags with multiple variants (not just on/off).
 * Add new experiment flags here and they'll automatically get a dropdown in the debug menu.
 *
 * Format:
 * - key: The feature flag key as it appears in the context
 * - label: Display name for the section header
 * - defaultValue: Value to use when flag is undefined
 * - variants: Array of { value, label } for dropdown options
 */
const VARIANT_FLAGS: Record<
  string,
  {
    label: string;
    defaultValue: string;
    variants: Array<{ value: string; label: string }>;
  }
> = {};

const SettingsMenu = () => {
  const [isDebugButtonVisible, setIsDebugButtonVisible] = useState(false);
  const [isDebugMenuOpen, setIsDebugMenuOpen] = useState(false);
  const [buttonPosition, setButtonPosition] = useState({ x: 20, y: 80 });
  const [revokeStatus, setRevokeStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");
  // In-app revoke confirmation dialog (replaces window.confirm)
  const [isRevokeConfirmOpen, setIsRevokeConfirmOpen] = useState(false);
  // In-app snackbar for feedback (replaces window.alert)
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const hasMoved = useRef(false);
  const isProd = import.meta.env.MODE === "production";
  const ffContext = useNubankFeatureFlagsContext();
  const navigate = useNavigate();

  // Handle drag start (mouse and touch)
  const handleDragStart = useCallback(
    (clientX: number, clientY: number) => {
      isDragging.current = true;
      hasMoved.current = false;
      dragStart.current = {
        x: clientX - (window.innerWidth - buttonPosition.x - 56),
        y: clientY - buttonPosition.y,
      };
    },
    [buttonPosition]
  );

  // Handle drag move
  const handleDragMove = useCallback((clientX: number, clientY: number) => {
    if (!isDragging.current) return;
    hasMoved.current = true;

    const newX = window.innerWidth - clientX + dragStart.current.x - 56;
    const newY = clientY - dragStart.current.y;

    // Keep button within viewport bounds
    setButtonPosition({
      x: Math.max(10, Math.min(window.innerWidth - 66, newX)),
      y: Math.max(10, Math.min(window.innerHeight - 66, newY)),
    });
  }, []);

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    isDragging.current = false;
  }, []);

  // Mouse event handlers
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      handleDragStart(e.clientX, e.clientY);

      const handleMouseMove = (e: MouseEvent) =>
        handleDragMove(e.clientX, e.clientY);
      const handleMouseUp = () => {
        handleDragEnd();
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };

      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    },
    [handleDragStart, handleDragMove, handleDragEnd]
  );

  // Touch event handlers
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.touches[0];
      handleDragStart(touch.clientX, touch.clientY);
    },
    [handleDragStart]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.touches[0];
      handleDragMove(touch.clientX, touch.clientY);
    },
    [handleDragMove]
  );

  const handleTouchEnd = useCallback(() => {
    handleDragEnd();
  }, [handleDragEnd]);

  // Handle click - only open menu if we didn't drag
  const handleClick = useCallback(() => {
    if (!hasMoved.current) {
      setIsDebugMenuOpen((prev) => !prev);
    }
  }, []);

  // Add F1 hotkey to open the debug menu in dev/staging
  useHotkeys("F1", () => {
    if (!isProd) {
      setIsDebugButtonVisible((prev) => !prev);
    }
  });

  // Mobile gesture: 5 taps in top-right corner (within 2 seconds) to toggle debug button.
  // Uses top-right to avoid overlap with bottom-right navigation buttons (e.g. "continue").
  const tapCount = useRef(0);
  const tapTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isDebugFlagEnabled = ffContext.enableDebugMenuAccess;

  useEffect(() => {
    const handleTap = (e: TouchEvent) => {
      if (!e.touches?.length) return;
      const touch = e.touches[0];
      const isTopRight =
        touch.clientX > window.innerWidth - 100 && touch.clientY < 100;

      if (isTopRight) {
        tapCount.current += 1;

        // Start the reset timer on the first tap only — all 5 taps must
        // land within 2 seconds of the first one.
        if (tapCount.current === 1) {
          if (tapTimer.current) {
            clearTimeout(tapTimer.current);
          }
          tapTimer.current = setTimeout(() => {
            tapCount.current = 0;
            tapTimer.current = null;
          }, 2000);
        }

        if (tapCount.current >= 5) {
          tapCount.current = 0;
          if (tapTimer.current) {
            clearTimeout(tapTimer.current);
            tapTimer.current = null;
          }
          if (canAccessDebugMenu(isProd, isDebugFlagEnabled)) {
            setIsDebugButtonVisible((prev) => !prev);
          }
        }
      }
    };

    document.addEventListener("touchstart", handleTap);
    return () => {
      document.removeEventListener("touchstart", handleTap);
      if (tapTimer.current) {
        clearTimeout(tapTimer.current);
      }
    };
  }, [isProd, isDebugFlagEnabled]);

  // F3 for XState inspector
  useHotkeys("F3", () => {
    if (!isProd) {
      inspect({
        iframe: false,
      });
    }
  });

  // Revoke NuPay: use in-app confirm dialog instead of window.confirm (blocked in webviews)
  const handleRevokeNupayClick = useCallback(() => {
    setIsRevokeConfirmOpen(true);
  }, []);

  const handleRevokeNupayConfirm = useCallback(async () => {
    setIsRevokeConfirmOpen(false);
    setRevokeStatus("loading");
    try {
      await revokeUserAuth({ entity: "HopperBR" });
      setRevokeStatus("success");
      setSnackbarMessage("NuPay enrollment revoked successfully.");
      setTimeout(() => setRevokeStatus("idle"), 3000);
    } catch (err) {
      console.error("Failed to revoke NuPay enrollment:", err);
      setRevokeStatus("error");
      setSnackbarMessage(
        "Failed to revoke NuPay enrollment. Please try again."
      );
      setTimeout(() => setRevokeStatus("idle"), 3000);
    }
  }, []);

  const handleClearSurveyCompletions = useCallback(() => {
    clearBookingSurveyCompletions();
    setSnackbarMessage(
      "CSAT survey completions cleared (hotels + flights legacy)."
    );
  }, []);

  // Floating debug menu button (draggable)
  const Debug = useMemo(() => {
    return (
      <Fab
        color="secondary"
        aria-label="debug"
        className={clsx(styles.debugButton, styles.draggable)}
        style={{
          right: buttonPosition.x,
          top: buttonPosition.y,
        }}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        size="medium"
      >
        <IconComponent name={IconName.Gear} ariaLabel="Settings" />
      </Fab>
    );
  }, [
    buttonPosition,
    handleClick,
    handleMouseDown,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  ]);

  // Get all flags directly from LaunchDarkly via ExperimentsContext
  const { experiments } = useExperiments();
  const { setExperimentOverride, resetExperimentOverrides } =
    useNubankExperimentOverrides();

  const handleResetOverrides = useCallback(() => {
    ffContext.resetOverrides();
    resetExperimentOverrides();
  }, [ffContext, resetExperimentOverrides]);

  const handleExperimentToggle = useCallback(
    (flagName: string) => {
      const currentVariant = experiments[flagName];
      const nextVariant = isExperimentVariantAvailable(currentVariant)
        ? ExperimentVariant.Control
        : ExperimentVariant.Available;
      setExperimentOverride(flagName, nextVariant);
    },
    [experiments, setExperimentOverride]
  );

  // Get list of variant flag keys to exclude from boolean toggles
  const variantFlagKeys = useMemo(() => Object.keys(VARIANT_FLAGS), []);

  // Boolean flags: from LD experiments (all boolean-valued flags)
  const flagWithOverrides = useMemo(() => {
    const ldBooleanFlags = Object.entries(experiments).filter(
      ([key, value]) =>
        (typeof value === "boolean" || value === undefined || value === null) &&
        !variantFlagKeys.includes(key)
    );
    // Also include any local context overrides for non-LD flags
    const contextBooleanFlags = Object.entries(ffContext).filter(
      ([key, value]) =>
        (typeof value === "boolean" || value === undefined || value === null) &&
        key !== "setOverride" &&
        key !== "resetOverrides" &&
        !variantFlagKeys.includes(key) &&
        !(key in experiments)
    );
    return [...ldBooleanFlags, ...contextBooleanFlags];
  }, [experiments, ffContext, variantFlagKeys]);

  const revokeButtonLabel =
    revokeStatus === "loading"
      ? "Revoking..."
      : revokeStatus === "success"
        ? "Revoked"
        : "Disconnect NuPay";

  const DrawerList = (
    <Box
      sx={{ width: 350 }}
      role="presentation"
      display="flex"
      flexDirection="column"
      justifyContent="flex-start"
    >
      <Box className={styles.drawerHeader}>
        <Typography variant="h6">Debug Menu</Typography>
        {!isProd && (
          <Typography variant="caption">
            F1: Menu | F2: Theme | F3: XState
          </Typography>
        )}
      </Box>
      <Button onClick={handleResetOverrides} className={styles.resetButton}>
        Reset to server values
      </Button>
      <Divider />
      <Box className={styles.devToolsSection}>
        <Typography variant="subtitle2" className={styles.sectionTitle}>
          Dev Tools
        </Typography>
        {!isProd && (
          <>
            <Button
              className={styles.devToolButton}
              onClick={() => {
                navigate("/dev/error-config");
                setIsDebugMenuOpen(false);
              }}
            >
              Error Config Viewer
            </Button>
            <Button
              className={styles.devToolButton}
              onClick={() => {
                window.dispatchEvent(
                  new CustomEvent("toggle-mock-checkout-error")
                );
                setIsDebugMenuOpen(false);
              }}
            >
              Mock API Errors
            </Button>
          </>
        )}
        <Button
          className={styles.devToolButton}
          onClick={handleRevokeNupayClick}
          disabled={revokeStatus === "loading" || revokeStatus === "success"}
        >
          {revokeButtonLabel}
        </Button>
        <Button
          className={styles.devToolButton}
          onClick={handleClearSurveyCompletions}
        >
          Clear CSAT survey completions
        </Button>
      </Box>
      <Divider />
      {/* Render dropdowns for manually configured variant flags */}
      {Object.entries(VARIANT_FLAGS).map(([flagKey, config]) => (
        <Box key={flagKey}>
          <Box className={styles.devToolsSection}>
            <Typography variant="subtitle2" className={styles.sectionTitle}>
              {config.label}
            </Typography>
            <FormControl
              fullWidth
              size="small"
              className={styles.variantSelect}
            >
              <InputLabel id={`${flagKey}-label`}>Variant</InputLabel>
              <Select
                labelId={`${flagKey}-label`}
                value={
                  (ffContext as Record<string, unknown>)[flagKey] ||
                  config.defaultValue
                }
                label="Variant"
                onChange={(e) => {
                  ffContext.setOverride(flagKey, e.target.value as string);
                }}
              >
                {config.variants.map((variant) => (
                  <MenuItem key={variant.value} value={variant.value}>
                    {variant.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Divider />
        </Box>
      ))}
      {!isProd &&
        (DEBUG_EXPERIMENT_TOGGLES.length > 0 ||
          flagWithOverrides.length > 0) && (
          <>
            <Box className={styles.devToolsSection}>
              <Typography variant="subtitle2" className={styles.sectionTitle}>
                Feature flags
              </Typography>
              <List disablePadding>
                {DEBUG_EXPERIMENT_TOGGLES.map(({ flagName, label }) => (
                  <ListItem key={flagName} disableGutters>
                    <FeatureFlagSlider
                      isEnabled={isExperimentVariantAvailable(
                        experiments[flagName]
                      )}
                      onClick={() => handleExperimentToggle(flagName)}
                      featureKey={label}
                    />
                  </ListItem>
                ))}
                {flagWithOverrides.map(([ffName, value]) => (
                  <ListItem key={ffName} disableGutters>
                    <FeatureFlagSlider
                      isEnabled={!!value}
                      onClick={() => ffContext.setOverride(ffName, !value)}
                      featureKey={ffName}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
            <Divider />
          </>
        )}
    </Box>
  );

  return (
    <>
      {isDebugButtonVisible ? Debug : null}
      <Drawer
        open={isDebugMenuOpen}
        onClose={() => setIsDebugMenuOpen(false)}
        anchor="right"
      >
        {DrawerList}
      </Drawer>

      {/* Revoke NuPay confirmation dialog — replaces window.confirm */}
      <Dialog
        open={isRevokeConfirmOpen}
        onClose={() => setIsRevokeConfirmOpen(false)}
        PaperProps={{ style: { borderRadius: 12, padding: 8 } }}
        aria-labelledby="revoke-dialog-title"
        aria-describedby="revoke-dialog-description"
      >
        <DialogTitle id="revoke-dialog-title">Disconnect NuPay</DialogTitle>
        <DialogContent>
          <Typography variant="body2" id="revoke-dialog-description">
            This will disconnect your NuPay enrollment. You will need to
            re-authorize on your next purchase. Continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsRevokeConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleRevokeNupayConfirm} color="primary">
            Disconnect
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feedback snackbar — replaces window.alert */}
      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={4000}
        onClose={() => setSnackbarMessage("")}
        message={snackbarMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        ContentProps={{
          role: "status",
          "aria-live": "polite",
        }}
      />
    </>
  );
};

const FeatureFlagSlider = ({
  isEnabled,
  onClick,
  featureKey,
}: {
  isEnabled: boolean;
  onClick: () => void;
  featureKey: string;
}) => {
  return (
    <Box className={styles.flagItem}>
      <Typography variant="body2">{featureKey}</Typography>
      <Switch checked={isEnabled} onClick={onClick} color="primary" />
    </Box>
  );
};

export default SettingsMenu;
